/**
 * /support — Support Center, and /support/:topicId — one FAQ topic.
 *
 * Both states came from the monolith's `view === "support"` block, which switched
 * on an `activeTopic` string. That string is now the URL, so a topic is linkable
 * and Back returns to the Center instead of leaving the Support area entirely.
 *
 * The search covers question and answer text as well as topic titles — a Phase 1
 * fix that is preserved here. Search state is route-local: nothing outside this
 * screen reads it.
 *
 * SIMULATED. FAQ content is a bundled fixture (src/data/faq.ts); there is no
 * search index and no help API.
 */
import { useEffect, useMemo, useState } from 'react';
import { BookOpen, Search, Ticket } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Accordion, BackLink, Btn, SectionHeading } from '../components/ui';
import { FAQ_TOPICS } from '../data/faq';
import { useAuthStore } from '../stores/authStore';
import { c } from '../styles/theme';
import { paths } from './paths';

/** One question matched by the search, with the topic it lives in. */
interface FaqMatch {
  topicId: string;
  topicTitle: string;
  index: number;
  q: string;
}

/** Muted caption line above a list. */
function Caption({ children }: { children: string }) {
  return <p style={{ fontSize: 12, color: c.text3, margin: '0 0 8px' }}>{children}</p>;
}

/**
 * "Still stuck? / Didn't find your answer?" panel.
 *
 * The main Support Center also places a visual-guide link immediately after the panel.
 */
function TicketCta({ title, showGuideLink = false }: { title: string; showGuideLink?: boolean }) {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return (
    <>
      <div
        style={{
          background: `linear-gradient(135deg, ${c.surface2}, ${c.surface3})`,
          borderRadius: 12,
          padding: '16px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <p
            style={{
              fontWeight: 600,
              fontSize: 14,
              margin: '0 0 4px',
              fontFamily: 'Space Grotesk',
              color: isAuthenticated ? c.text1 : '#FFFFFF',
            }}
          >
            {title}
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>
            Contact the support team
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Btn
            variant="secondary"
            onClick={() => navigate(paths.ticket)}
            style={{
              flexShrink: 0,
              borderColor: c.accent,
            }}
          >
            <Ticket size={14} />
            Open a ticket
          </Btn>
        </div>
      </div>
      {showGuideLink && (
        <Link
          to={paths.visualGuide}
          className="lyx-guide-visual-link"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 10,
            color: c.accent,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <BookOpen size={14} aria-hidden="true" />
          View guide
        </Link>
      )}
    </>
  );
}

/** Shared card style for the search-result and topic buttons. */
const CARD_BUTTON = {
  textAlign: 'left',
  border: `1px solid ${c.border}`,
  borderRadius: 10,
  padding: '12px 14px',
  background: c.surface1,
  cursor: 'pointer',
} as const;

/** /support — the Center: search, matching answers, topic grid. */
export default function Support() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();

  /*
    Searches the question and answer text of every FAQ entry across all topics, in
    addition to topic titles and descriptions. The prototype's filter only looked
    at title/desc, so a term appearing solely inside an answer — "refund" is the
    clearest example — matched nothing.
  */
  const matchingFaqs = useMemo<FaqMatch[]>(() => {
    if (!query) return [];
    return FAQ_TOPICS.flatMap((topic) =>
      topic.questions
        .map((faq, index) => ({ faq, index }))
        .filter(({ faq }) => `${faq.q} ${faq.a}`.toLowerCase().includes(query))
        .map(({ faq, index }) => ({
          topicId: topic.id,
          topicTitle: topic.title,
          index,
          q: faq.q,
        }))
    );
  }, [query]);

  const matchingTopics = useMemo(() => {
    if (!query) return FAQ_TOPICS;
    return FAQ_TOPICS.filter(
      (f) =>
        f.title.toLowerCase().includes(query) ||
        f.desc.toLowerCase().includes(query) ||
        f.questions.some((faq) => `${faq.q} ${faq.a}`.toLowerCase().includes(query))
    );
  }, [query]);

  return (
    <div>
      <SectionHeading
        as="h1"
        title="Support Center"
        subtitle="Search articles or browse by topic"
        marginBottom={16}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          border: `1px solid ${c.border}`,
          borderRadius: 10,
          padding: '10px 12px',
          background: c.surface1,
          marginBottom: 20,
        }}
      >
        <Search size={14} color={c.text3} aria-hidden="true" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={'Search for help — e.g. "credits", "billing", "export"'}
          // The prototype's search input had no label of any kind.
          aria-label="Search support articles"
          style={{
            border: 'none',
            background: 'transparent',
            color: c.text1,
            fontSize: 13,
            width: '100%',
          }}
        />
      </div>

      {/* Matching questions are listed directly so the answer is one click away
          rather than requiring the user to guess which topic contains it. The
          matched question's index is passed through so the topic page opens it. */}
      {matchingFaqs.length > 0 && (
        <>
          <Caption>
            {`${matchingFaqs.length} matching ${matchingFaqs.length === 1 ? 'answer' : 'answers'}`}
          </Caption>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
            {matchingFaqs.map((m) => (
              <button
                key={`${m.topicId}-${m.index}`}
                type="button"
                onClick={() => navigate(`${paths.supportTopic(m.topicId)}?q=${m.index}`)}
                style={CARD_BUTTON}
              >
                <p style={{ fontWeight: 500, fontSize: 13, margin: '0 0 3px', color: c.text1 }}>
                  {m.q}
                </p>
                <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>{m.topicTitle}</p>
              </button>
            ))}
          </div>
        </>
      )}

      {query && matchingFaqs.length === 0 && matchingTopics.length === 0 && (
        <div
          style={{
            border: `1px solid ${c.border}`,
            borderRadius: 10,
            padding: '14px 16px',
            background: c.surface1,
            marginBottom: 22,
          }}
        >
          <p style={{ fontSize: 13, margin: '0 0 4px', color: c.text1 }}>
            No results for &ldquo;{search.trim()}&rdquo;
          </p>
          <p style={{ fontSize: 12, color: c.text3, margin: 0 }}>
            Try a different term, or open a ticket below and we&apos;ll help directly.
          </p>
        </div>
      )}

      {matchingTopics.length > 0 && (
        <>
          <Caption>Browse by topic</Caption>
          <div className="lyx-grid-2" style={{ marginBottom: 22 }}>
            {matchingTopics.map((f) => {
              const Icon = f.icon;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => navigate(paths.supportTopic(f.id))}
                  style={CARD_BUTTON}
                >
                  {Icon && <Icon size={16} color={c.text2} style={{ marginBottom: 6 }} />}
                  <p style={{ fontWeight: 500, fontSize: 13, margin: '0 0 3px', color: c.text1 }}>
                    {f.title}
                  </p>
                  <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>{f.desc}</p>
                </button>
              );
            })}
          </div>
        </>
      )}

      <TicketCta title="Still stuck?" showGuideLink />
    </div>
  );
}

/**
 * /support/:topicId — one topic's questions as an accordion.
 *
 * The initially-open question comes from a `?q=` search param, which is how a
 * search result deep-links straight to its answer. Defaults to the first question,
 * as the prototype did.
 */
export function SupportTopic() {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const topic = FAQ_TOPICS.find((f) => f.id === topicId);

  // Read once, as the initial value: after this the accordion is user-controlled
  // and re-reading the URL on every render would fight the user's clicks.
  const [openIndex, setOpenIndex] = useState(() => {
    const q = Number(new URLSearchParams(window.location.search).get('q'));
    return Number.isInteger(q) && q >= 0 ? q : 0;
  });

  /*
    An unknown topic id — a stale link, a typo, or an id renamed in data/faq.ts — returns
    to the Center rather than throwing. The prototype indexed FAQ_TOPICS.find(...)
    unguarded.

    IN AN EFFECT, WHICH IS THE WHOLE FIX. This called `navigate()` in the render body, and
    react-router drops a navigation requested during render: `useNavigate` guards on a ref
    it only sets true in a layout effect, in the production bundle as well as in
    development. So the redirect never happened, `return null` rendered nothing, and
    nothing changed to trigger another attempt — /support/<unknown> was a permanently
    blank page at a URL that stayed in the address bar, with no way out except the
    sidebar. The comment above claimed a behaviour the code could not perform.

    `replace` so Back does not bounce straight into the missing topic again — the same
    shape and the same reasoning as Studio.tsx's and Playlist.tsx's deep-link guards,
    which is where this pattern was already written down correctly twice.
  */
  useEffect(() => {
    if (!topic) navigate(paths.support, { replace: true });
  }, [topic, navigate]);

  if (!topic) return null;

  const Icon = topic.icon;

  return (
    <div>
      <BackLink fallbackTo={paths.support} fallbackLabel="Support Center" mode="parent" />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            background: c.accentSoft,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {Icon && <Icon size={16} color={c.accent} />}
        </div>
        <h1
          style={{
            fontFamily: 'Space Grotesk',
            fontWeight: 600,
            fontSize: 16,
            margin: 0,
            color: c.text1,
          }}
        >
          {topic.title}
        </h1>
      </div>
      <p style={{ fontSize: 12, color: c.text3, margin: '4px 0 18px 42px' }}>{topic.desc}</p>

      <Accordion
        items={topic.questions.map((item) => ({ id: item.q, title: item.q, body: item.a }))}
        openIndex={openIndex}
        // -1 collapses, matching the prototype's sentinel.
        onToggle={(i) => setOpenIndex(openIndex === i ? -1 : i)}
        marginBottom={20}
      />

      <TicketCta title="Didn't find your answer?" />
    </div>
  );
}
